/**
 * "Signaler": a plain-text report the user copies and sends where they like. No server, and only
 * the line concerned, never the whole pattern.
 */
import type { DiagnosticGroup } from '../diagnostics/group';
import { diagnosticMessage } from '../engine/localize';
import type { CompileResult } from '../engine/types';
import { translate, type Lang } from '../i18n';
import { sizesLabel } from './sizes';

export type ReportKind = 'false-positive' | 'misread' | 'general';

export interface ReportInput {
  kind: ReportKind;
  lang: Lang;
  group?: DiagnosticGroup;
  result?: CompileResult | null;
  engineVersion?: string | null;
  schemaVersion?: string | null;
}

export function buildReport({ kind, lang, group, result, engineVersion, schemaVersion }: ReportInput): string {
  const t = (key: Parameters<typeof translate>[1], vars?: Record<string, string | number>) => translate(lang, key, vars);
  const kindLabel = kind === 'false-positive' ? t('find.reportFalse') : kind === 'misread' ? t('find.reportMisread') : t('report.general');
  const lines = [`${t('report.header')}`, `${t('report.kind')} : ${kindLabel}`];
  if (group) {
    lines.push(`${t('report.code')} : ${group.code}`);
    const text = result?.lines.find((l) => l.line_no === group.line_no)?.text;
    if (group.line_no > 0) lines.push(`${t('report.line', { n: group.line_no })} : ${text ?? ''}`.trimEnd());
    lines.push(`${t('report.sizes')} : ${sizesLabel(group.sizes, result ?? null, (k, v) => t(k, v), lang)}`);
    const messages = [...new Set(group.diagnostics.map((d) => diagnosticMessage(d, lang).text))];
    for (const m of messages) lines.push(`${t('report.message')} : ${m}`);
  }
  const versions = [engineVersion && `stitchcore ${engineVersion}`, schemaVersion && `schema ${schemaVersion}`].filter(Boolean).join(' · ');
  if (versions) lines.push(`${t('report.engine')} : ${versions}`);
  lines.push('', t('report.note'), '');
  return (lang === 'en' ? lines.map((l) => l.replace(/ : /, ': ')) : lines).join('\n');
}
